
import { useState, useEffect } from "react";
import { useTranslations } from "use-intl";
import { BRAND_WORDMARK } from "@financialharmony/ui/brand";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Check, X } from "lucide-react";
import SignupForm from "@/components/auth/SignupForm";
import OAuthButtons from "@/components/auth/OAuthButtons";
import { isSocialLoginEnabled } from "@/config/features";
import { AuthLanguageSwitch } from "@/components/i18n/AuthLanguageSwitch";
import { userFacingErrorMessage } from "@/utils/userFacingError";

// The password rules, in the order they are listed; each is a key of `consultantAuth.reset.rules`.
const PASSWORD_RULES = ["length", "uppercase", "lowercase", "number", "special"] as const;

// A sign-in refusal is either one of our own sentences (a key) or GoTrue's own message, shown as sent.
type LoginError = { key: "invalidCredentials" | "accountNotFound" } | { message: string };

const Auth = () => {
  const t = useTranslations("consultantAuth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(() => !isSocialLoginEnabled());
  const { signIn, signUp, signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check for password reset link
  useEffect(() => {
    const resetParam = searchParams.get('reset');
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');

    if (resetParam === 'true' || (accessToken && refreshToken)) {
      setIsPasswordResetMode(true);
    }
  }, [searchParams]);

  // ProtectedRoute sends a signed-out visitor here with the page they asked for in router
  // state; once signed in they return to it (path and query), otherwise to the overview.
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const returnTo = from?.pathname && from.pathname.startsWith("/") && from.pathname !== "/auth"
    ? `${from.pathname}${from.search ?? ""}`
    : "/overview";

  useEffect(() => {
    if (user && !isPasswordResetMode) {
      navigate(returnTo, { replace: true });
    }
  }, [user, navigate, isPasswordResetMode, returnTo]);

  const handleAuthentication = async (type: "login" | "signup") => {
    if (!email || !password) {
      toast.error(t("login.errors.missingCredentials"));
      return;
    }

    setIsLoading(true);
    setLoginError(null);
    setShowPasswordReset(false);
    
    try {
      const response = type === "login" 
        ? await signIn(email, password)
        : await signUp(email, password);
      
      const { error } = response;

      if (error) {
        if (type === "login") {
          // Handle specific login errors
          if (error.message.includes("Invalid login credentials") || 
              error.message.includes("Email not confirmed") ||
              error.message.includes("Invalid credentials")) {
            setLoginError({ key: "invalidCredentials" });
            setShowPasswordReset(true);
          } else if (error.message.includes("Email not found") || 
                     error.message.includes("User not found")) {
            setLoginError({ key: "accountNotFound" });
            setShowPasswordReset(false);
          } else {
            setLoginError({ message: userFacingErrorMessage(error, t("login.errors.logInFailed")) });
            setShowPasswordReset(true);
          }
        }
        throw error;
      }
      
      // Check if we have a session after auth. The redirect itself is handled by the
      // `useEffect([user])` above, which fires as soon as `user` is set — no separate
      // navigate needed here.
      if (response.data.session) {
        toast.success(type === "login" ? t("login.success") : t("signup.created"));
      } else if (type === "signup") {
        toast.success(t("signup.created"));
      }
    } catch (error: any) {
      if (type !== "login") {
        toast.error(userFacingErrorMessage(error, t("login.errors.failed")));
      }
      console.error("Auth error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast.error(t("reset.enterEmailFirst"));
      return;
    }

    setIsResettingPassword(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`
      });

      if (error) {
        throw error;
      }

      toast.success(t("reset.emailSent"));
      setShowPasswordReset(false);
      setLoginError(null);
    } catch (error: any) {
      toast.error(userFacingErrorMessage(error, t("reset.emailFailed")));
      console.error("Password reset error:", error);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    strength = Object.values(checks).filter(Boolean).length;
    return { strength, checks };
  };

  const handleNewPasswordSubmit = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error(t("reset.enterBoth"));
      return;
    }

    const passwordStrength = getPasswordStrength(newPassword);
    const isPasswordValid = passwordStrength.strength >= 3 && newPassword.length >= 8;
    const passwordsMatch = newPassword === confirmNewPassword;

    if (!isPasswordValid) {
      toast.error(t("reset.weak"));
      return;
    }

    if (!passwordsMatch) {
      toast.error(t("reset.mismatch"));
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      toast.success(t("reset.updated"));
      // The recovery link's session is still active at this point (updateUser doesn't sign the
      // user out) — the "already authenticated" effect above would otherwise re-fire the moment
      // isPasswordResetMode flips false and bounce this navigate("/auth") straight to /overview
      // (issue #897). Sign out first so `user` is genuinely null by the time that effect runs,
      // requiring an explicit fresh sign-in with the new password — the correct behavior after a
      // password-recovery flow anyway.
      await signOut();
      setIsPasswordResetMode(false);
      setNewPassword("");
      setConfirmNewPassword("");
      // Clear URL parameters
      navigate("/auth", { replace: true });
    } catch (error: any) {
      toast.error(userFacingErrorMessage(error, t("reset.updateFailed")));
      console.error("Password update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show password reset form if coming from reset link
  if (isPasswordResetMode) {
    const passwordStrength = getPasswordStrength(newPassword);
    const isPasswordValid = passwordStrength.strength >= 3 && newPassword.length >= 8;
    const passwordsMatch = newPassword === confirmNewPassword && confirmNewPassword.length > 0;

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-full max-w-md p-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">{t("reset.title")}</h1>
            <p className="text-muted-foreground">{t("reset.subtitle")}</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t("reset.cardTitle")}</CardTitle>
              <CardDescription>{t("reset.cardDescription")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="new-password" className="text-sm font-medium">
                  {t("reset.newPassword")}
                </label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                
                {newPassword && (
                  <div className="space-y-1">
                    {PASSWORD_RULES.map((rule) => (
                      <div key={rule} className="flex items-center space-x-2 text-xs">
                        {passwordStrength.checks[rule] ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <X className="h-3 w-3 text-red-500" />
                        )}
                        <span className={passwordStrength.checks[rule] ? "text-green-600" : "text-red-600"}>
                          {t(`reset.rules.${rule}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm-new-password" className="text-sm font-medium">
                  {t("reset.confirmPassword")}
                </label>
                <div className="relative">
                  <Input
                    id="confirm-new-password"
                    type={showConfirmNewPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  >
                    {showConfirmNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {confirmNewPassword && (
                  <div className="flex items-center space-x-2 text-xs">
                    {passwordsMatch ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <X className="h-3 w-3 text-red-500" />
                    )}
                    <span className={passwordsMatch ? "text-green-600" : "text-red-600"}>
                      {t("reset.rules.match")}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full" 
                onClick={handleNewPasswordSubmit}
                disabled={!isPasswordValid || !passwordsMatch || isLoading}
              >
                {isLoading ? t("reset.submitting") : t("reset.submit")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  const showEmailPasswordAuth = import.meta.env.VITE_ENABLE_EMAIL_PASSWORD_AUTH === 'true';

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <AuthLanguageSwitch />
      <div className="w-full max-w-md p-6">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="bg-black rounded-md p-1.5 flex items-center justify-center w-8 h-8">
              <div className="bg-white rounded-full w-4 h-4"></div>
            </div>
            <h1 className="text-2xl font-bold">{BRAND_WORDMARK}</h1>
          </div>
          <p className="text-muted-foreground">{t("tagline")}</p>
        </div>

        {!showEmailAuth ? (
          /* OAuth Buttons with Email option */
          <OAuthButtons
            onEmailClick={() => setShowEmailAuth(true)}
            showEmailButton={showEmailPasswordAuth}
          />
        ) : (
          /* Email/Password Auth Form */
          <>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid grid-cols-2 w-full mb-4">
                <TabsTrigger value="login">{t("login.tab")}</TabsTrigger>
                <TabsTrigger value="signup">{t("signup.tab")}</TabsTrigger>
              </TabsList>

          <TabsContent value="login">
            <Card>
              <CardHeader>
                <CardTitle>{t("login.cardTitle")}</CardTitle>
                <CardDescription>{t("login.cardDescription")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    {t("login.email")}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("login.emailPlaceholder")}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setLoginError(null);
                      setShowPasswordReset(false);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">
                    {t("login.password")}
                  </label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setLoginError(null);
                      setShowPasswordReset(false);
                    }}
                  />
                </div>
                
                {loginError && (
                  <div className="space-y-2">
                    <p className="text-sm text-red-600">{"key" in loginError ? t(`login.errors.${loginError.key}`) : loginError.message}</p>
                    {showPasswordReset && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePasswordReset}
                        disabled={isResettingPassword}
                        className="w-full"
                      >
                        {isResettingPassword ? t("login.sendingReset") : t("login.resetPassword")}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button 
                  className="w-full" 
                  onClick={() => handleAuthentication("login")}
                  disabled={isLoading}
                >
                  {isLoading ? t("login.submitting") : t("login.submit")}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="signup">
            <SignupForm />
          </TabsContent>
        </Tabs>

            {isSocialLoginEnabled() && (
              <Button
                variant="ghost"
                className="w-full mt-4"
                onClick={() => setShowEmailAuth(false)}
              >
                {t("login.backToOptions")}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Auth;
